import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, FormTextField } from '../../../components/common';
import { Select } from 'components/common/Select';
import { TextField } from 'components/common/TextField';
import { ISelectOption } from '../../../components/common/Select/types';
import { addressToOptionTransformer } from '../../ApplicationFormPage/AddAddress';
import { addAddressQuestions } from '../../ApplicationFormPage/AddAddress/questions';
import { useAddressesByPostcode } from '../../../queries';
import { mapPostcodeAddressToForm } from './mappers';
import {
  addressFieldPathsFor,
  isAddressManualPathFor,
  orgAddressValuePath,
  type BrokerOrgAddressSection,
} from './addressFieldPaths';
import { SignupOrganisationFormFields } from './signupOrganisationForm.types';
import { OrganisationAddressForm } from './types/address';

type AddressFieldsProps = {
  group: BrokerOrgAddressSection;
  disabled?: boolean;
};

const inputClass = { sx: { padding: '6px !important' } as const, className: 'text-sm !font-normal' as const };

export function AddressFields({ group, disabled = false }: AddressFieldsProps) {
  const form = useFormContext<SignupOrganisationFormFields>();

  const intl = useIntl();
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const p = useMemo(() => addressFieldPathsFor(group), [group]);
  const fullAddressName = useMemo(() => orgAddressValuePath(group), [group]);
  const manualName = isAddressManualPathFor(group);

  const isManual = useWatch({
    name: manualName,
    control: form.control,
    defaultValue: false,
  }) as boolean;
  const postcode =
    (useWatch({
      name: p.postcode,
      control: form.control,
      defaultValue: '',
    }) as string) || '';

  const { data: addresses = [], refetch: fetchAddressesByPostcode } = useAddressesByPostcode(postcode);
  const addressOptions = useMemo(() => addresses.map(addressToOptionTransformer), [addresses]);
  const addressBlock = useWatch({ name: fullAddressName, control: form.control }) as
    | OrganisationAddressForm
    | undefined;

  const [listSelection, setListSelection] = useState<string>('');

  const setGroupAddress = (body: OrganisationAddressForm) => {
    form.setValue(fullAddressName, body, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const setManualHandler = () => {
    form.setValue(manualName, true);
    setListSelection('');
  };

  const setHandlerByPostcode = () => {
    form.setValue(manualName, false);
  };

  useEffect(() => {
    if (addressOptions.length > 0 && !isManual && !addressBlock?.addressLine1) {
      const v = String(addressOptions[0].value);
      setListSelection(v);
      const first = addresses.find(a => a.fullAddress === v);
      if (first) {
        setGroupAddress(mapPostcodeAddressToForm(first));
      }
    }
  }, [addressOptions, isManual, addressBlock?.addressLine1, addresses]);

  return (
    <ul className='list-none pl-0 col-span-1 md:col-span-2'>
      <li>
        <FormattedMessage id={addAddressQuestions.postcode.labelKey} />
        <div className='flex items-center justify-between gap-3'>
          <FormTextField
            name={p.postcode}
            rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
            disabled={disabled}
            textFieldProps={{
              type: 'text',
              inputProps: {
                'aria-label': intl.formatMessage({ id: addAddressQuestions.postcode.labelKey }),
                ...inputClass,
              },
            }}
          />
          {!isManual && (
            <div>
              <Button
                className='w-40 h-8'
                variant='outlined'
                disabled={!postcode.length || Boolean(disabled)}
                onClick={() => fetchAddressesByPostcode()}
              >
                <FormattedMessage id='applicationForm.ADD_ADDRESS.findAddress' />
              </Button>
            </div>
          )}
        </div>
      </li>
      {isManual ? (
        <>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.address1.labelKey} />
            <FormTextField
              name={p.addressLine1}
              rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.address1.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.address2.labelKey} />
            <FormTextField
              name={p.addressLine2}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.address2.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.city.labelKey} />
            <FormTextField
              name={p.town}
              rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.city.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <span className='mb-1 block text-sm'>
              <FormattedMessage id='applicationForm.ADD_ADDRESS.county.label' />
            </span>
            <FormTextField
              name={p.county}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: { ...inputClass },
              }}
            />
          </li>
          <li className='mt-2 w-full'>
            <FormattedMessage id={addAddressQuestions.country.labelKey} />
            <FormTextField
              name={p.country}
              rules={disabled ? undefined : { required: { value: true, message: requiredFieldErrorMessage } }}
              disabled={disabled}
              textFieldProps={{
                type: 'text',
                inputProps: {
                  'aria-label': intl.formatMessage({ id: addAddressQuestions.country.labelKey }),
                  ...inputClass,
                },
              }}
            />
          </li>
          {!disabled && (
            <p className='cursor-pointer font-semibold text-sky-500 underline' onClick={setHandlerByPostcode}>
              <FormattedMessage id='applicationForm.ADD_ADDRESS.wantToFindAddress' />
            </p>
          )}
        </>
      ) : (
        <>
          <div>
            <FormattedMessage id={addAddressQuestions.fullAddress.labelKey} />
            <div className='mt-1'>
              <Select
                disableClearable
                className='w-full'
                options={addressOptions}
                filterOptions={(options: ISelectOption[]) => options}
                value={addressOptions.find(o => o.value === listSelection) || addressOptions[0] || null}
                onChange={(_event: React.SyntheticEvent, option: ISelectOption | null) => {
                  if (!option) {
                    return;
                  }
                  setListSelection(String(option.value));
                  const found = addresses.find(a => a.fullAddress === String(option.value));
                  if (found) {
                    setGroupAddress(mapPostcodeAddressToForm(found));
                  }
                }}
                disabled={disabled}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      style: { cursor: 'pointer' },
                      readOnly: 'readonly',
                    }}
                    InputProps={{
                      ...params.InputProps,
                      sx: { padding: '0 !important' },
                    }}
                  />
                )}
              />
            </div>
          </div>
          {!disabled && (
            <p className='mt-2 cursor-pointer font-semibold text-sky-500 underline' onClick={setManualHandler}>
              <FormattedMessage id='applicationForm.ADD_ADDRESS.cantFindAddress' />
            </p>
          )}
        </>
      )}
    </ul>
  );
}
